import React from 'react'
import { format, addMinutes } from 'date-fns'
import { Box, Text, Flex } from '@chakra-ui/react'

import {
    formatClinicianName,
    getClientFullName,
    stringIfExists,
} from '~utils/helpers'
import { formatPhoneReadable } from '~utils/phone'
import {
    ActionItemFieldsFragment,
    ActionItemStatus,
    namedOperations,
    useUpdateActionItemMutation,
} from '~graphql/generated/graphql'
import { Button } from '@chakra-ui/react'
import MissingEntityDrawer from '~components/pending-actions/pending-item-rows/missing-entity-drawer'
import { SimpleGrid, useDisclosure } from '@chakra-ui/react'
import { useNamedDisclosure } from '~utils/hooks/use-named-disclosure'
import { useActionItems } from '~contexts/action-item-provider'
import ActionConfirmation from '~components/shared/modals/action-confirmation'
import PendingActionDetail from '~components/pending-actions/pending-action-detail'
import { FaPaw, FaUser } from 'react-icons/fa'
import { useClinic } from '~contexts/clinic-context'

interface Props {
    actionItem: ActionItemFieldsFragment
}

export default function MissingClientContent({ actionItem }: Props) {
    const { clinic } = useClinic()
    const { refetch } = useActionItems()
    const { isOpen, onOpen, onClose } = useDisclosure()
    const [isAlertOpen, onAlertOpen, onAlertClose] = useNamedDisclosure()

    const [updateActionItem, { loading: updateLoading }] =
        useUpdateActionItemMutation({
            variables: {
                id: actionItem.id,
                data: {
                    status: ActionItemStatus.Completed,
                },
            },
            refetchQueries: [namedOperations.Query.countActionItems],
            onCompleted: () => refetch(),
        })

    async function handleUpdate() {
        await updateActionItem()
    }

    if (!actionItem.pending_appointment) return <></>

    const {
        client_info,
        patient_info,
        service,
        service_name,
        clinician,
        start_time,
        minutes,
    } = actionItem.pending_appointment

    return (
        <>
            <Box w="full">
                <Flex
                    justify="space-between"
                    alignItems="center"
                    w="full"
                    direction={{ base: 'column', md: 'row' }}
                    gap={3}
                >
                    <Box flex={1}>
                        <Text fontSize="sm" fontWeight="semibold">
                            We couldn't match{' '}
                            {stringIfExists(client_info?.first_name, {
                                posttext: stringIfExists(
                                    client_info?.last_name,
                                    {
                                        pretext: ' ',
                                    },
                                ),
                            })}{' '}
                            to a client in your system.
                        </Text>
                        <Text fontSize="xs" color="gray.600">
                            {stringIfExists(service?.name || service_name, {
                                pretext: 'For ',
                            })}{' '}
                            {stringIfExists(formatClinicianName(clinician), {
                                pretext: 'with ',
                            })}{' '}
                            from {format(start_time, 'h:mm a')} -{' '}
                            {format(
                                addMinutes(start_time, minutes),
                                "h:mm a 'on' MMMM d",
                            )}
                        </Text>
                    </Box>
                    <Flex
                        gap={2}
                        flexDirection={{ base: 'row-reverse', md: 'column' }}
                        w={{ base: 'full', md: 'max-content' }}
                    >
                        <Button
                            size="xs"
                            shadow="sm"
                            onClick={onAlertOpen}
                            w="full"
                        >
                            Mark as resolved
                        </Button>
                        <Button
                            size="xs"
                            colorScheme="brand"
                            shadow="sm"
                            w="full"
                            onClick={onOpen}
                        >
                            Assign Client
                        </Button>
                    </Flex>
                </Flex>
            </Box>
            <MissingEntityDrawer
                isOpen={isOpen}
                onClose={onClose}
                actionItem={actionItem}
            />
            <ActionConfirmation
                isOpen={isAlertOpen}
                onClose={onAlertClose}
                onConfirm={handleUpdate}
                isLoading={updateLoading}
                title="Mark as resolved?"
                size="xl"
            >
                <Box>
                    <Text fontWeight="semibold" mb={1}>
                        Are you sure you want to mark this as resolved?
                    </Text>
                    <Text fontSize="sm">
                        Please ensure that a valid client and patient have been
                        assigned to the appointment in your practice management
                        system.
                    </Text>
                    <SimpleGrid columns={2} gap={2} mt={4}>
                        <PendingActionDetail
                            icon={FaUser}
                            label="Client Info"
                            value={getClientFullName(client_info, true) || ''}
                            helpText={
                                formatPhoneReadable(
                                    client_info?.phone,
                                    clinic?.address?.country,
                                ) || undefined
                            }
                        />
                        <PendingActionDetail
                            icon={FaPaw}
                            label="Patient Info"
                            value={`${patient_info?.patient_name}`}
                            helpText={patient_info?.patient_breed || undefined}
                        />
                    </SimpleGrid>
                </Box>
            </ActionConfirmation>
        </>
    )
}
